import { useState, useEffect, useCallback } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import apiClient from "@/utils/apiClient";
import { Search, Shield, ShieldOff, Ban, CheckCircle, ChevronLeft, ChevronRight, Trash2, UserX, AlertTriangle, X, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const ROLE_COLORS = {
  owner: "bg-red-500/10 text-red-400 border border-red-500/20",
  super_admin: "bg-amber-500/10 text-amber-400 border border-amber-500/20",
  admin: "bg-blue-500/10 text-blue-400 border border-blue-500/20",
  moderator: "bg-violet-500/10 text-violet-400 border border-violet-500/20",
  support: "bg-teal-500/10 text-teal-400 border border-teal-500/20",
  user: "bg-white/5 text-white/40",
};

const ROLE_LABELS = {
  owner: "Owner",
  super_admin: "Super Admin",
  admin: "Admin",
  moderator: "Moderator",
  support: "Support",
  user: "User",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [roleInfo, setRoleInfo] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null); // {type: 'single'|'bulk', userId?, userName?}
  const [deleting, setDeleting] = useState(false);
  const [roleDropdown, setRoleDropdown] = useState(null); // user_id of open dropdown
  const [toast, setToast] = useState(null);
  const limit = 15;

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit };
      if (search) params.search = search;
      const res = await apiClient.get('/admin/users', { params });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [skip, search, limit]);

  const fetchRoleInfo = useCallback(async () => {
    try {
      const res = await apiClient.get('/admin/roles');
      setRoleInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch role info:', err);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchRoleInfo(); }, [fetchRoleInfo]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    fetchUsers();
  };

  const updateRole = async (userId, newRole) => {
    try {
      await apiClient.put(`/admin/users/${userId}/role`, { role: newRole });
      showToast(`Role updated to ${ROLE_LABELS[newRole]}`);
      setRoleDropdown(null);
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to update role", "error");
    }
  };

  const updateStatus = async (userId, newStatus) => {
    try {
      await apiClient.put(`/admin/users/${userId}/status`, { status: newStatus });
      showToast(`Status updated to ${newStatus}`);
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to update status", "error");
    }
  };

  const handleDeleteSingle = async () => {
    if (!deleteModal?.userId) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/admin/users/${deleteModal.userId}`);
      showToast("User deleted");
      setDeleteModal(null);
      setSelectedIds(prev => prev.filter(id => id !== deleteModal.userId));
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to delete user", "error");
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    setDeleting(true);
    try {
      const res = await apiClient.post('/admin/users/bulk-delete', { user_ids: selectedIds });
      showToast(res.data.message);
      setDeleteModal(null);
      setSelectedIds([]);
      fetchUsers();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to delete users", "error");
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (userId) => {
    setSelectedIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    const selectableUsers = users.filter(u => u.role !== "owner");
    if (selectedIds.length === selectableUsers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableUsers.map(u => u.user_id));
    }
  };

  const canDelete = roleInfo?.can_delete;
  const currentRole = roleInfo?.current_role || "user";
  const currentLevel = roleInfo?.current_level || 0;
  const assignableRoles = roleInfo?.assignable_roles || [];

  // Build the role options this admin can assign
  const getRoleOptions = (targetUser) => {
    const targetLevel = (ROLE_LABELS[targetUser.role] ? { owner: 5, super_admin: 4, admin: 3, moderator: 2, support: 1, user: 0 }[targetUser.role] : 0) || 0;
    // Can't change users at or above your level (unless owner)
    if (currentRole !== "owner" && targetLevel >= currentLevel) return [];
    const options = ["user", ...assignableRoles];
    if (currentRole === "owner") {
      return ["user", "support", "moderator", "admin", "super_admin"].filter(r => r !== targetUser.role);
    }
    return options.filter(r => r !== targetUser.role);
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-users">
        {/* Toast */}
        {toast && (
          <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl text-sm font-medium shadow-lg transition-all ${
            toast.type === "error" ? "bg-red-500 text-white" : "bg-emerald-500 text-white"
          }`} data-testid="toast-message">
            {toast.msg}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="delete-modal">
            <div className="bg-[#1a1d2e] border border-white/10 rounded-2xl p-6 max-w-md w-full">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-white font-semibold">Confirm Deletion</h3>
              </div>
              <p className="text-white/60 text-sm mb-6">
                {deleteModal.type === "single"
                  ? <>Are you sure you want to delete <strong className="text-white">{deleteModal.userName}</strong>? This cannot be undone.</>
                  : <>Are you sure you want to delete <strong className="text-white">{selectedIds.length} users</strong>? This cannot be undone. Owner and higher-role accounts will be skipped.</>
                }
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  className="border-white/10 text-white/50 hover:text-white rounded-xl"
                  onClick={() => setDeleteModal(null)}
                  disabled={deleting}
                  data-testid="cancel-delete-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red-500 hover:bg-red-600 text-white rounded-xl"
                  onClick={deleteModal.type === "single" ? handleDeleteSingle : handleBulkDelete}
                  disabled={deleting}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? "Deleting..." : `Delete ${deleteModal.type === "bulk" ? `${selectedIds.length} Users` : "User"}`}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-white mb-1">User Management</h1>
            <div className="flex items-center gap-3">
              <p className="text-white/40 text-sm">{total} total users</p>
              {roleInfo && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${ROLE_COLORS[currentRole]}`}>
                  You: {ROLE_LABELS[currentRole]}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canDelete && selectedIds.length > 0 && (
              <Button
                className="bg-red-500 hover:bg-red-600 text-white rounded-xl gap-2"
                onClick={() => setDeleteModal({ type: "bulk" })}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4" /> Delete {selectedIds.length}
              </Button>
            )}
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  type="text"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-[#161821] border-white/10 text-white placeholder:text-white/30 w-64 rounded-xl"
                  data-testid="user-search-input"
                />
              </div>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl" data-testid="user-search-btn">
                Search
              </Button>
            </form>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            <div className="bg-[#161821] border border-white/5 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/30 text-xs uppercase border-b border-white/5">
                      {canDelete && (
                        <th className="p-4 w-10">
                          <input
                            type="checkbox"
                            checked={selectedIds.length > 0 && selectedIds.length === users.filter(u => u.role !== "owner").length}
                            onChange={toggleSelectAll}
                            className="rounded border-white/20 bg-transparent accent-amber-500"
                            data-testid="select-all-checkbox"
                          />
                        </th>
                      )}
                      <th className="text-left p-4">User</th>
                      <th className="text-left p-4">Email</th>
                      <th className="text-left p-4">Coins</th>
                      <th className="text-left p-4">Tier</th>
                      <th className="text-left p-4">Role</th>
                      <th className="text-left p-4">Status</th>
                      <th className="text-left p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => {
                      const roleOptions = getRoleOptions(u);
                      const isProtected = u.role === "owner";
                      return (
                        <tr
                          key={u.user_id}
                          className={`border-b border-white/5 last:border-0 hover:bg-white/[0.02] ${selectedIds.includes(u.user_id) ? "bg-amber-500/5" : ""}`}
                          data-testid={`user-row-${u.user_id}`}
                        >
                          {canDelete && (
                            <td className="p-4">
                              {!isProtected && (
                                <input
                                  type="checkbox"
                                  checked={selectedIds.includes(u.user_id)}
                                  onChange={() => toggleSelect(u.user_id)}
                                  className="rounded border-white/20 bg-transparent accent-amber-500"
                                  data-testid={`select-user-${u.user_id}`}
                                />
                              )}
                            </td>
                          )}
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white/50 text-sm font-medium">
                                {u.name?.charAt(0) || "?"}
                              </div>
                              <span className="text-white/80">{u.name}</span>
                            </div>
                          </td>
                          <td className="p-4 text-white/50">{u.email}</td>
                          <td className="p-4 text-amber-400">{u.smahoo_coins ?? 0}</td>
                          <td className="p-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              u.subscription_tier === 'elite' ? 'bg-violet-500/10 text-violet-400' :
                              u.subscription_tier === 'premium' ? 'bg-blue-500/10 text-blue-400' :
                              u.subscription_tier === 'basic' ? 'bg-emerald-500/10 text-emerald-400' :
                              u.subscription_tier === 'admin' ? 'bg-amber-500/10 text-amber-400' :
                              'bg-white/5 text-white/40'
                            }`}>
                              {u.subscription_tier || "free"}
                            </span>
                          </td>
                          <td className="p-4 relative">
                            <div className="relative inline-block">
                              <button
                                onClick={() => roleOptions.length > 0 ? setRoleDropdown(roleDropdown === u.user_id ? null : u.user_id) : null}
                                className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 ${ROLE_COLORS[u.role] || ROLE_COLORS.user} ${roleOptions.length > 0 ? "cursor-pointer hover:opacity-80" : "cursor-default"}`}
                                data-testid={`role-badge-${u.user_id}`}
                              >
                                {ROLE_LABELS[u.role] || u.role || "User"}
                                {roleOptions.length > 0 && <ChevronDown className="w-3 h-3" />}
                              </button>
                              {roleDropdown === u.user_id && roleOptions.length > 0 && (
                                <div className="absolute top-full left-0 mt-1 bg-[#1a1d2e] border border-white/10 rounded-xl shadow-xl z-20 py-1 min-w-[140px]" data-testid={`role-dropdown-${u.user_id}`}>
                                  {roleOptions.map(r => (
                                    <button
                                      key={r}
                                      onClick={() => updateRole(u.user_id, r)}
                                      className="w-full text-left px-3 py-2 text-xs text-white/70 hover:bg-white/5 hover:text-white transition-colors flex items-center gap-2"
                                      data-testid={`set-role-${r}-${u.user_id}`}
                                    >
                                      <span className={`w-2 h-2 rounded-full ${
                                        r === "super_admin" ? "bg-amber-400" :
                                        r === "admin" ? "bg-blue-400" :
                                        r === "moderator" ? "bg-violet-400" :
                                        r === "support" ? "bg-teal-400" : "bg-white/30"
                                      }`}></span>
                                      {ROLE_LABELS[r]}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              u.status === 'active' || !u.status ? 'bg-emerald-500/10 text-emerald-400' :
                              u.status === 'suspended' ? 'bg-red-500/10 text-red-400' :
                              'bg-white/5 text-white/40'
                            }`}>
                              {u.status || "active"}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1">
                              {/* Status toggle */}
                              {!isProtected && currentLevel >= 3 && (
                                <>
                                  {u.status !== 'suspended' ? (
                                    <button
                                      onClick={() => updateStatus(u.user_id, 'suspended')}
                                      className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/30 hover:text-red-400 transition-colors"
                                      title="Suspend User"
                                      data-testid={`suspend-${u.user_id}`}
                                    >
                                      <Ban className="w-4 h-4" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => updateStatus(u.user_id, 'active')}
                                      className="p-1.5 rounded-lg hover:bg-emerald-500/10 text-white/30 hover:text-emerald-400 transition-colors"
                                      title="Reactivate User"
                                      data-testid={`activate-${u.user_id}`}
                                    >
                                      <CheckCircle className="w-4 h-4" />
                                    </button>
                                  )}
                                </>
                              )}
                              {/* Delete button */}
                              {canDelete && !isProtected && (
                                <button
                                  onClick={() => setDeleteModal({ type: "single", userId: u.user_id, userName: u.email })}
                                  className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/30 hover:text-red-400 transition-colors"
                                  title="Delete User"
                                  data-testid={`delete-${u.user_id}`}
                                >
                                  <UserX className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {users.length === 0 && (
                      <tr>
                        <td colSpan={canDelete ? 8 : 7} className="p-8 text-center text-white/30">No users found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Role Legend */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="text-white/30 text-xs">Roles:</span>
              {Object.entries(ROLE_LABELS).map(([key, label]) => (
                <span key={key} className={`px-2 py-0.5 rounded-full text-[10px] ${ROLE_COLORS[key]}`}>
                  {label}
                </span>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <p className="text-white/40 text-sm">Page {currentPage} of {totalPages}</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    disabled={skip === 0}
                    className="border-white/10 text-white/50 hover:text-white rounded-xl"
                    data-testid="prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSkip(skip + limit)}
                    disabled={skip + limit >= total}
                    className="border-white/10 text-white/50 hover:text-white rounded-xl"
                    data-testid="next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
